import { useState } from 'react'
import { FaStar, FaFire, FaLeaf, FaArrowRight, FaPepperHot } from 'react-icons/fa'

const DEMO_ITEMS = [
  { category: 'classic', name: 'Mixte Tandoori Chicken', description: 'Succulent tandoori chicken with fresh lettuce, tomatoes, and mint mayo', price: 'DA 550', popular: true, tags: ['Bestseller'], image: '/menu/mixte.png', emoji: '🥪' },
  { category: 'spicy', name: 'Spicy Tikka Masala', description: 'Chicken tikka pieces in rich masala sauce with onions and peppers', price: 'DA 600', popular: true, tags: ['Signature'], image: '/menu/tandoori.png', emoji: '🌶️' },
  { category: 'premium', name: 'Royal Tandoori Supreme', description: 'Double tandoori chicken with cheese, avocado and special sauce', price: 'DA 750', popular: false, tags: ['Premium'], image: '/menu/tacos.png', emoji: '👑' },
]

const MenuSection = ({ restaurant }) => {
  const [activeCategory, setActiveCategory] = useState('all')

  const color = restaurant?.primaryColor || '#ff6b00'

  // Build items from real data or fall back to demo
  const rawItems = restaurant?.menu?.length > 0
    ? restaurant.menu.map(item => ({
        id: item.id,
        category: item.category?.toLowerCase() || 'other',
        name: item.name,
        description: item.description || '',
        price: `DA ${item.price?.toFixed ? item.price.toFixed(0) : item.price}`,
        popular: false,
        tags: item.compositions?.map(c => c.name).slice(0, 2) || [],
        image: item.imageUrl || null,
        emoji: item.emoji || '🍽️',
      }))
    : DEMO_ITEMS

  // Derive categories from actual items
  const uniqueCats = [...new Set(rawItems.map(i => i.category).filter(Boolean))]
  const categories = [
    { id: 'all', name: 'All' },
    ...uniqueCats.map(c => ({ id: c, name: c.charAt(0).toUpperCase() + c.slice(1) })),
  ]

  const menuItems = rawItems
  const filteredItems = activeCategory === 'all'
    ? menuItems
    : menuItems.filter(item => item.category === activeCategory)

  return (
    <>
      <section id="menu" style={{
        padding: '40px 20px',
        backgroundColor: '#fff',
        position: 'relative'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          {/* Section Header */}
          <div style={{
            textAlign: 'center',
            marginBottom: '40px'
          }}>          
            <h2 style={{
              fontSize: '48px',
              fontWeight: 'bold',
              color: '#000',
              margin: '0 0 15px 0',
              lineHeight: '1.2'
            }}>
              Our <span style={{ color: color }}>{restaurant?.name || 'Famous'} Menu</span>
            </h2>
          </div>

          {/* Category Filter */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '12px',
            marginBottom: '40px',
            flexWrap: 'wrap'
          }}>
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                style={{
                  padding: '10px 25px',
                  backgroundColor: activeCategory === category.id ? color : 'transparent',
                  color: activeCategory === category.id ? 'white' : '#333',
                  border: `2px solid ${activeCategory === category.id ? color : '#e0e0e0'}`,
                  borderRadius: '25px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '15px',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  ':hover': {
                    borderColor: '#ff6b00',
                    color: activeCategory === category.id ? 'white' : '#ff6b00',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                {category.id === 'spicy' && <FaPepperHot size={14} />}
                {category.id === 'vegetarian' && <FaLeaf size={14} />}
                {category.id === 'premium' && <FaStar size={14} />}
                {category.name}
              </button>
            ))}
          </div>

          {/* Menu Items Grid - 3 per line on desktop */}
          <div className="menu-grid">
            {filteredItems.map((item, index) => (
              <div key={index} className="menu-card">
                {/* Image Container */}
                <div className="image-container">
                  {item.image ? (
                    <img src={item.image} alt={item.name} className="menu-image" />
                  ) : (
                    <div className="menu-image" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '64px', background: '#111' }}>
                      {item.emoji || '🍽️'}
                    </div>
                  )}

                  {/* Popular Badge */}
                  {item.popular && (
                    <div className="popular-badge" style={{ backgroundColor: color }}>
                      <FaStar size={10} /> Bestseller
                    </div>
                  )}

                  {/* Price Tag */}
                  <div className="price-tag" style={{ color: color, borderColor: color }}>
                    {item.price}
                  </div>
                </div>
                
                {/* Content */}
                <div className="menu-content">
                  <div className="menu-header">
                    <div style={{ flex: 1 }}>
                      <h3 className="menu-title">
                        {item.name}
                      </h3>
                      
                      <div className="tags-container">
                        {item.tags.map((tag, idx) => (
                          <span key={idx} className={`menu-tag ${tag.includes('Spicy') || tag.includes('Hot') ? 'spicy-tag' : 'regular-tag'}`}>
                            {tag.includes('Spicy') || tag.includes('Hot') ? <FaPepperHot size={10} /> : null}
                            {tag.includes('Vegetarian') || tag.includes('Vegan') ? <FaLeaf size={10} /> : null}
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <p className="menu-description">
                    {item.description}
                  </p>
                  
                  {/* Footer Info */}
                  <div className="menu-footer">
                    <div className="nutrition-info">
                      <span className="calories">
                        <FaFire color={color} /> {item.category === 'vegetarian' ? 'Vegetarian' : 'Fresh'}
                      </span>
                    </div>
                    <button className="order-button" style={{ color: color, borderColor: color }}>
                      Order Now <FaArrowRight size={12} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <style jsx>{`
        /* Menu Grid - 3 per line on desktop, responsive on mobile */
        .menu-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 30px;
          margin-bottom: 40px;
        }
        
        /* Menu Card */
        .menu-card {
          background-color: #fff;
          border-radius: 15px;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          border: 1px solid #f0f0f0;
        }
        
        .menu-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12);
        }
        
        /* Image Container */
        .image-container {
          position: relative;
          height: 220px;
          overflow: hidden;
        }
        
        .menu-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }
        
        .menu-card:hover .menu-image {
          transform: scale(1.05);
        }
        
        /* Badges */
        .popular-badge {
          position: absolute;
          top: 15px;
          right: 15px;
          background-color: #ff6b00;
          color: white;
          padding: 6px 15px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 5px;
          box-shadow: 0 4px 12px rgba(255, 107, 0, 0.3);
        }
        
        .spice-badge {
          position: absolute;
          top: 15px;
          left: 15px;
          background-color: #dc2626;
          color: white;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 5px;
        }
        
        .price-tag {
          position: absolute;
          bottom: -20px;
          right: 20px;
          background-color: #fff;
          color: #ff6b00;
          width: 60px;
          height: 60px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          font-weight: bold;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
          border: 2px solid #ff6b00;
        }
        
        /* Content */
        .menu-content {
          padding: 25px;
        }
        
        .menu-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 15px;
        }
        
        .menu-title {
          font-size: 20px;
          font-weight: bold;
          color: #000;
          margin: 0 0 8px 0;
          line-height: 1.3;
        }
        
        .tags-container {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          margin-bottom: 15px;
        }
        
        .menu-tag {
          padding: 4px 12px;
          border-radius: 15px;
          font-size: 12px;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        
        .regular-tag {
          background-color: rgba(255, 107, 0, 0.1);
          color: #ff6b00;
        }
        
        .spicy-tag {
          background-color: rgba(220, 38, 38, 0.1);
          color: #dc2626;
        }
        
        .menu-description {
          color: #666;
          margin-bottom: 20px;
          font-size: 14px;
          line-height: 1.6;
        }
        
        /* Footer */
        .menu-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 15px;
          border-top: 1px solid #f0f0f0;
        }
        
        .nutrition-info {
          display: flex;
          align-items: center;
          gap: 15px;
        }
        
        .calories, .diet {
          display: flex;
          align-items: center;
          gap: 5px;
          color: #666;
          font-size: 14px;
        }
        
        .order-button {
          background-color: transparent;
          color: #ff6b00;
          border: 2px solid #ff6b00;
          padding: 8px 20px;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s;
        }
        
        .order-button:hover {
          background-color: #ff6b00;
          color: white;
          transform: translateX(5px);
        }
        
        /* ===== MOBILE RESPONSIVE STYLES ===== */
        
        /* Tablet (768px and below) */
        @media (max-width: 1024px) {
          .menu-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 25px;
          }
          
          #menu {
            padding: 30px 20px;
          }
          
          h2 {
            font-size: 36px !important;
          }
        }
        
        /* Mobile (640px and below) */
        @media (max-width: 640px) {
          .menu-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }
          
          #menu {
            padding: 20px 15px;
          }
          
          h2 {
            font-size: 28px !important;
            margin-bottom: 10px !important;
          }
          
          .category-filter {
            gap: 8px;
            margin-bottom: 30px;
          }
          
          .category-button {
            padding: 8px 15px;
            font-size: 13px;
          }
          
          .image-container {
            height: 180px;
          }
          
          .popular-badge, .spice-badge {
            top: 10px;
            padding: 5px 10px;
            font-size: 11px;
          }
          
          .popular-badge {
            right: 10px;
          }
          
          .spice-badge {
            left: 10px;
          }
          
          .price-tag {
            width: 50px;
            height: 50px;
            font-size: 16px;
            bottom: -15px;
            right: 15px;
          }
          
          .menu-content {
            padding: 20px;
          }
          
          .menu-title {
            font-size: 18px;
          }
          
          .menu-tag {
            padding: 3px 10px;
            font-size: 11px;
          }
          
          .menu-description {
            font-size: 13px;
            margin-bottom: 15px;
          }
          
          .menu-footer {
            flex-direction: column;
            gap: 15px;
            align-items: stretch;
          }
          
          .order-button {
            width: 100%;
            justify-content: center;
          }
          
          .nutrition-info {
            justify-content: center;
          }
        }
        
        /* Small Mobile (480px and below) */
        @media (max-width: 480px) {
          h2 {
            font-size: 24px !important;
          }
          
          .category-button {
            padding: 6px 12px;
            font-size: 12px;
          }
          
          .image-container {
            height: 160px;
          }
          
          .menu-content {
            padding: 15px;
          }
        }
      `}</style>
    </>
  )
}

export default MenuSection